import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Bell, Check, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * Widget de Última Notificación Push pendiente de leer.
 * Complementa la notificación push de OneSignal: aunque el push no llegue
 * (cache, permisos, dispositivo apagado), al entrar al Dashboard el usuario
 * ve la última notificación importante y puede marcarla como leída.
 *
 * - Solo se renderiza si hay una notificación NO leída
 * - Encola por orden FIFO (más antigua primero)
 * - Al tocar "Leído", se marca y carga la siguiente automáticamente
 * - En mobile: como el widget se posiciona PRIMERO en el grid del Dashboard,
 *   aparece al principio de la pantalla
 */
const UltimaNotificacionWidget = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notif, setNotif] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  const loadNotif = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notificaciones/pendiente-dashboard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setNotif(res.data?.notificacion || null);
    } catch (e) {
      // Silent: si falla, no mostramos el widget
      setNotif(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    if (!user) return;
    loadNotif();
    const interval = setInterval(loadNotif, 30000); // refrescar cada 30s
    return () => clearInterval(interval);
  }, [user, loadNotif]);

  const handleMarcarLeida = async () => {
    if (!notif?.id || marking) return;
    setMarking(true);
    try {
      await axios.put(
        `${API}/notificaciones/${notif.id}/marcar-leida`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      // Cargar la siguiente notificación pendiente (encolado)
      await loadNotif();
    } catch (e) {
      toast.error('No se pudo marcar como leída');
    } finally {
      setMarking(false);
    }
  };

  const handleAbrir = () => {
    if (notif?.url) {
      navigate(notif.url);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      const date = new Date(iso);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffMins < 1) return 'Recién';
      if (diffMins < 60) return `Hace ${diffMins} min`;
      if (diffHours < 24) return `Hace ${diffHours} h`;
      if (diffDays < 7) return `Hace ${diffDays} d`;
      return date.toLocaleDateString('es-UY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  // No renderizar si está cargando o no hay notificación pendiente
  if (loading || !notif) return null;

  return (
    <Card
      className="overflow-hidden shadow-lg border-0 bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50 hover:shadow-xl transition-shadow"
      data-testid="ultima-notificacion-widget"
    >
      <CardContent className="p-0 h-full flex flex-col">
        {/* Header con icono campana animado */}
        <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-rose-500 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Bell className="h-4 w-4 animate-bounce" />
            <span className="text-xs font-semibold uppercase tracking-wide">Notificación</span>
          </div>
          <span className="text-[10px] text-white/90 bg-white/20 px-2 py-0.5 rounded-full">
            {formatTime(notif.fecha_creacion)}
          </span>
        </div>

        {/* Cuerpo */}
        <div
          className="flex-1 px-4 py-3 flex flex-col cursor-pointer overflow-hidden"
          onClick={handleAbrir}
          role={notif.url ? 'button' : undefined}
        >
          <h3
            className="font-bold text-sm text-gray-800 mb-1.5 line-clamp-2"
            data-testid="ultima-notif-titulo"
          >
            {notif.titulo || 'Nueva notificación'}
          </h3>
          <p
            className="text-xs text-gray-600 leading-relaxed line-clamp-4 flex-1"
            data-testid="ultima-notif-mensaje"
          >
            {notif.mensaje}
          </p>
        </div>

        {/* Botón Leído */}
        <div className="px-4 pb-3 pt-1">
          <Button
            onClick={handleMarcarLeida}
            disabled={marking}
            size="sm"
            className="w-full bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white text-xs font-semibold shadow-md"
            data-testid="ultima-notif-leido-btn"
          >
            {marking ? (
              <><RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Marcando...</>
            ) : (
              <><Check className="h-3 w-3 mr-1" /> Leído</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default UltimaNotificacionWidget;
